import React, { useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow, differenceInCalendarDays } from 'date-fns';
import {
  useCohort,
  useCohortProgress,
  useCohortStudents,
  useCohortCourses,
  useUpdateCohort,
  useArchiveCohort,
  useDeleteCohort,
  useRemoveStudent,
  useEnrollStudents,
  useBulkEnroll,
} from '../hooks';
import type { CohortInput, CohortStatus } from '../services/cohortsApi';
import styles from './CohortDetail.module.css';

type Tab = 'students' | 'progress' | 'activity';

const CohortDetail: React.FC = () => {
  const { cohortId = '' } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();

  const { data: cohort, isLoading, isError, refetch } = useCohort(cohortId);
  const { data: progress } = useCohortProgress(cohortId);
  const { data: studentRows } = useCohortStudents(cohortId);
  const { data: courses } = useCohortCourses();

  const updateCohort = useUpdateCohort();
  const archiveCohort = useArchiveCohort();
  const deleteCohort = useDeleteCohort();
  const removeStudent = useRemoveStudent();
  const enrollStudents = useEnrollStudents();
  const bulkEnroll = useBulkEnroll();

  const [tab, setTab] = useState<Tab>('students');
  const [showEdit, setShowEdit] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAddStudents, setShowAddStudents] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);
  const [addStudentInput, setAddStudentInput] = useState('');
  const [form, setForm] = useState<CohortInput | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <span>Loading cohort...</span>
      </div>
    );
  }

  if (isError || !cohort) {
    return (
      <div className={styles.errorState}>
        <p>Cohort not found.</p>
        <Link to="/cohorts" className={styles.linkBtn}>
          Back to Cohorts
        </Link>
        <button className={styles.retryBtn} onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const openEdit = () => {
    setForm({
      name: cohort.name,
      description: cohort.description,
      courseId: cohort.courseId,
      capacity: cohort.capacity,
      startDate: cohort.startDate.slice(0, 10),
      endDate: cohort.endDate.slice(0, 10),
    });
    setShowEdit(true);
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    try {
      await updateCohort.mutateAsync({ id: cohort.id, input: form });
      setShowEdit(false);
    } catch {
      /* toast via hook */
    }
  };

  const confirmArchive = async () => {
    try {
      await archiveCohort.mutateAsync(cohort.id);
      setShowArchive(false);
    } catch {
      /* toast via hook */
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteCohort.mutateAsync(cohort.id);
      navigate('/cohorts');
    } catch {
      /* toast via hook */
    }
  };

  const handleRemoveStudent = async () => {
    if (!removeTarget) return;
    try {
      await removeStudent.mutateAsync({ cohortId: cohort.id, studentId: removeTarget.id });
      setRemoveTarget(null);
    } catch {
      /* toast via hook */
    }
  };

  const handleAddStudents = async (e: React.FormEvent) => {
    e.preventDefault();
    const ids = addStudentInput
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length === 0) return;
    try {
      await enrollStudents.mutateAsync({ cohortId: cohort.id, studentIds: ids });
      setAddStudentInput('');
      setShowAddStudents(false);
    } catch {
      /* toast via hook */
    }
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await bulkEnroll.mutateAsync({ cohortId: cohort.id, file });
      setShowAddStudents(false);
    } catch {
      /* toast via hook */
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const daysRemaining = differenceInCalendarDays(new Date(cohort.endDate), new Date());
  const formatDate = (iso: string) => {
    try {
      return format(new Date(iso), 'MMM d, yyyy');
    } catch {
      return iso;
    }
  };
  const statusBadge = (status: CohortStatus) => (
    <span className={`${styles.statusBadge} ${styles[`status_${status}`] || ''}`}>{status}</span>
  );

  return (
    <div className={styles.container}>
      {/* Breadcrumbs + Header */}
      <div className={styles.breadcrumbs}>
        <Link to="/cohorts">Cohorts</Link>
        <span>/</span>
        <span>{cohort.name}</span>
      </div>

      <header className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.headerTitleRow}>
            <h1>{cohort.name}</h1>
            {statusBadge(cohort.status)}
          </div>
          <p className={styles.headerSub}>
            {cohort.courseName} · {cohort.instructorName} · {formatDate(cohort.startDate)} →{' '}
            {formatDate(cohort.endDate)}
          </p>
          <p className={styles.headerDesc}>{cohort.description}</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.headerBtn} onClick={openEdit}>
            Edit
          </button>
          {cohort.status !== 'archived' && (
            <button className={styles.headerBtn} onClick={() => setShowArchive(true)}>
              Archive
            </button>
          )}
          <button
            className={`${styles.headerBtn} ${styles.danger}`}
            onClick={() => setShowDelete(true)}
          >
            Delete
          </button>
        </div>
      </header>

      {/* Stats */}
      <div className={styles.statsRow}>
        <StatCard label="Enrolled" value={`${cohort.studentCount} / ${cohort.capacity}`} />
        <StatCard label="Completion" value={`${cohort.completionRate}%`} accent="primary" />
        <StatCard label="Avg progress" value={`${cohort.avgProgress}%`} accent="success" />
        <StatCard
          label="Days remaining"
          value={daysRemaining >= 0 ? `${daysRemaining}` : 'Ended'}
          accent={daysRemaining < 7 && daysRemaining >= 0 ? 'warn' : undefined}
        />
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'students' ? styles.tabActive : ''}`}
          onClick={() => setTab('students')}
        >
          Students
        </button>
        <button
          className={`${styles.tab} ${tab === 'progress' ? styles.tabActive : ''}`}
          onClick={() => setTab('progress')}
        >
          Progress
        </button>
        <button
          className={`${styles.tab} ${tab === 'activity' ? styles.tabActive : ''}`}
          onClick={() => setTab('activity')}
        >
          Activity
        </button>
      </div>

      {/* Students tab */}
      {tab === 'students' && (
        <div className={styles.tabPanel}>
          <div className={styles.panelHeader}>
            <h2>Enrolled students ({studentRows?.length || 0})</h2>
            <button className={styles.primaryBtn} onClick={() => setShowAddStudents(true)}>
              + Add Students
            </button>
          </div>

          {!studentRows || studentRows.length === 0 ? (
            <div className={styles.emptyPanel}>
              <span className={styles.emptyIcon}>👥</span>
              <p>No students enrolled yet</p>
              <button className={styles.primaryBtn} onClick={() => setShowAddStudents(true)}>
                + Add Students
              </button>
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th>Last active</th>
                    <th>Enrolled</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {studentRows.map((s) => {
                    const pct = s.totalLabs
                      ? Math.round((s.completedLabs / s.totalLabs) * 100)
                      : 0;
                    return (
                      <tr key={s.studentId}>
                        <td>
                          <div className={styles.studentCell}>
                            <div className={styles.avatar}>
                              {s.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)}
                            </div>
                            <div>
                              <Link to={`/students/${s.studentId}`} className={styles.studentName}>
                                {s.name}
                              </Link>
                              <div className={styles.studentEmail}>{s.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className={styles.progressCell}>
                            <div className={styles.progressBar}>
                              <div
                                className={styles.progressFill}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className={styles.progressNum}>
                              {s.completedLabs}/{s.totalLabs}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`${styles.pillStatus} ${styles[`pill_${s.status}`]}`}>
                            {s.status}
                          </span>
                        </td>
                        <td className={styles.muted}>
                          {formatDistanceToNow(new Date(s.lastActive), { addSuffix: true })}
                        </td>
                        <td className={styles.muted}>{formatDate(s.enrolledAt)}</td>
                        <td>
                          <button
                            className={styles.rowBtnDanger}
                            onClick={() =>
                              setRemoveTarget({ id: s.studentId, name: s.name })
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Progress tab */}
      {tab === 'progress' && (
        <div className={styles.tabPanel}>
          <h2>Per-lab completion</h2>
          {!progress || progress.byLab.length === 0 ? (
            <div className={styles.emptyPanel}>
              <p>No progress data yet</p>
            </div>
          ) : (
            <div className={styles.labChart}>
              {progress.byLab.map((l) => (
                <div key={l.labId} className={styles.labRow}>
                  <div className={styles.labLabel}>{l.labTitle}</div>
                  <div className={styles.labBar}>
                    <div
                      className={styles.labFill}
                      style={{ width: `${l.completionRate}%` }}
                    />
                  </div>
                  <div className={styles.labPct}>{l.completionRate}%</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity tab (synthesized from progress for now) */}
      {tab === 'activity' && (
        <div className={styles.tabPanel}>
          <h2>Recent activity</h2>
          {!progress || progress.students.length === 0 ? (
            <div className={styles.emptyPanel}>
              <p>No recent activity</p>
            </div>
          ) : (
            <ul className={styles.activityList}>
              {progress.students.map((s) => (
                <li key={s.studentId} className={styles.activityItem}>
                  <div className={styles.activityDot} />
                  <div className={styles.activityBody}>
                    <div className={styles.activityText}>
                      <strong>{s.name}</strong> · {s.completedLabs} of {s.totalLabs} labs complete
                    </div>
                    <div className={styles.activityMeta}>
                      Last active{' '}
                      {formatDistanceToNow(new Date(s.lastActive), { addSuffix: true })}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && form && (
        <div className={styles.modalOverlay} onClick={() => setShowEdit(false)}>
          <div className={styles.formModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Edit Cohort</h2>
              <button className={styles.closeBtn} onClick={() => setShowEdit(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={submitEdit} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Course *</label>
                <select
                  value={form.courseId}
                  onChange={(e) => setForm({ ...form, courseId: e.target.value })}
                  required
                >
                  {courses?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Start date *</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>End date *</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Capacity</label>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={form.capacity}
                    onChange={(e) =>
                      setForm({ ...form, capacity: parseInt(e.target.value) || 1 })
                    }
                  />
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setShowEdit(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.primaryBtn}
                  disabled={updateCohort.isPending}
                >
                  {updateCohort.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archive Confirm */}
      {showArchive && (
        <div className={styles.modalOverlay} onClick={() => setShowArchive(false)}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>📦</div>
            <h3>Archive cohort?</h3>
            <p>
              <strong>{cohort.name}</strong> will be marked archived. You can restore it later.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={() => setShowArchive(false)}>
                Cancel
              </button>
              <button
                className={styles.archiveBtn}
                onClick={confirmArchive}
                disabled={archiveCohort.isPending}
              >
                {archiveCohort.isPending ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDelete(false)}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={`${styles.confirmIcon} ${styles.iconDanger}`}>⚠️</div>
            <h3>Delete cohort?</h3>
            <p>
              This permanently removes <strong>{cohort.name}</strong> and all enrollment records.
              This cannot be undone — prefer Archive if you may need this data later.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={() => setShowDelete(false)}>
                Cancel
              </button>
              <button
                className={styles.deleteBtn}
                onClick={confirmDelete}
                disabled={deleteCohort.isPending}
              >
                {deleteCohort.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Student Confirm */}
      {removeTarget && (
        <div className={styles.modalOverlay} onClick={() => setRemoveTarget(null)}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>👤</div>
            <h3>Remove student?</h3>
            <p>
              Remove <strong>{removeTarget.name}</strong> from this cohort? Their lab progress is
              kept but they will no longer receive cohort announcements.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={() => setRemoveTarget(null)}>
                Cancel
              </button>
              <button
                className={styles.deleteBtn}
                onClick={handleRemoveStudent}
                disabled={removeStudent.isPending}
              >
                {removeStudent.isPending ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Students Modal */}
      {showAddStudents && (
        <div className={styles.modalOverlay} onClick={() => setShowAddStudents(false)}>
          <div className={styles.formModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Add students</h2>
              <button className={styles.closeBtn} onClick={() => setShowAddStudents(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleAddStudents} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Student IDs or emails</label>
                <textarea
                  rows={4}
                  value={addStudentInput}
                  onChange={(e) => setAddStudentInput(e.target.value)}
                  placeholder="Paste one per line, or separate with commas — e.g. student-42, ali.khan@example.pk"
                />
                <p className={styles.hint}>
                  Tip: separate multiple entries with commas, spaces, semicolons, or new lines.
                </p>
              </div>

              <div className={styles.csvSection}>
                <h3>Or upload a CSV</h3>
                <p className={styles.hint}>
                  First column should contain student ID or email. Header row optional.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvUpload}
                  className={styles.fileInput}
                  disabled={bulkEnroll.isPending}
                />
                {bulkEnroll.isPending && (
                  <span className={styles.muted}>Uploading...</span>
                )}
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setShowAddStudents(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.primaryBtn}
                  disabled={
                    enrollStudents.isPending || !addStudentInput.trim()
                  }
                >
                  {enrollStudents.isPending ? 'Enrolling...' : 'Enroll'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: string;
  accent?: 'primary' | 'success' | 'warn';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, accent }) => (
  <div className={styles.statCard}>
    <span className={styles.statLabel}>{label}</span>
    <span className={`${styles.statValue} ${accent ? styles[`accent_${accent}`] : ''}`}>
      {value}
    </span>
  </div>
);

export default CohortDetail;
